import React, { useState, useEffect } from 'react';
import { Bug, X, Send, CheckCircle2 } from 'lucide-react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { useForm, ValidationError } from '@formspree/react';
import { Button } from '@/components/ui/Button';

export function BugReportModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [bugContent, setBugContent] = useState('');
  
  const [state, handleSubmit] = useForm("xkoylzar");

  useEffect(() => {
    if (state.succeeded) {
      setTimeout(() => {
        setIsOpen(false);
        setBugContent('');
      }, 2000);
    }
  }, [state.succeeded]);

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bugContent.trim()) return;
    handleSubmit(e as any);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors h-8 px-3 rounded-md cursor-pointer"
        aria-label="Report a Bug"
      >
        <Bug className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Report Bug</span>
      </button>

      <ResponsiveOverlay
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        variant="modal"
        className="sm:max-w-md w-full"
      >
        <div className="flex flex-col bg-white dark:bg-slate-900 w-full h-full sm:h-auto">
          <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
            <div className="flex items-center gap-2">
              <div className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-1.5 rounded-lg">
                <Bug className="w-4 h-4" />
              </div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">Report a Bug</h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              aria-label="Close Bug Report Modal"
              className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={onFormSubmit} className="flex flex-col flex-1 overflow-hidden h-full">
            <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
              {state.succeeded ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Bug Reported</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Thank you for reporting this issue! Our team will investigate.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="bugContent" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Describe the issue
                    </label>
                    <textarea
                      id="bugContent"
                      name="message"
                      className="w-full h-32 px-3 py-2 text-sm text-slate-900 dark:text-white bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none resize-none"
                      placeholder="What didn't work as expected? How can we reproduce it?"
                      value={bugContent}
                      onChange={(e) => setBugContent(e.target.value)}
                    />
                    <ValidationError 
                      prefix="Message" 
                      field="message"
                      errors={state.errors}
                    />
                  </div>
                </div>
              )}
            </div>

            {!state.succeeded && (
              <div className="p-4 sm:px-6 sm:py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!bugContent.trim() || state.submitting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {state.submitting ? 'Submitting...' : 'Submit Report'}
                  {!state.submitting && <Send className="w-4 h-4" />}
                </button>
              </div>
            )}
          </form>
        </div>
      </ResponsiveOverlay>
    </>
  );
}
